import { motion } from "framer-motion";

import { Button } from "@/atoms/button/Button";
import { Icon } from "@/atoms/icon/Icon";
import Book from "@/atoms/icon/icons/personalisation_book.svg";
import Heart from "@/atoms/icon/icons/personalisation_heart.svg";
import Users from "@/atoms/icon/icons/personalisation_users.svg";
import { TextButton } from "@/atoms/text-button/TextButton";
import { ANIMATION_DURATION } from "@/constants/animations.constants";
import { useTranslation } from "@/core/i18n";
import { usePlatform } from "hooks/usePlatform";
import { getPossessiveForm } from "utils/functions";

import { PersonalisationHeading } from "./PersonalisationHeading";
import { PersonalisationScreenLayout } from "./PersonalisationScreenLayout";

interface PersonalisationAdvisorBenefitsProps {
  onContinue: () => void;
  onSkip: () => void;
}

export const PersonalisationAdvisorBenefits = ({
  onContinue,
  onSkip,
}: PersonalisationAdvisorBenefitsProps) => {
  const { platform } = usePlatform();
  const { t } = useTranslation();

  const cards = [
    {
      icon: Users,
      text: t("personalisationInfoCards.cardPatientPlans"),
    },
    {
      icon: Book,
      text: t("personalisationInfoCards.cardTrainedOn", {
        possessiveName: getPossessiveForm(platform?.name),
      }),
    },
    {
      icon: Heart,
      text: t("personalisationInfoCards.cardPrivateData"),
    },
  ];

  return (
    <PersonalisationScreenLayout
      footer={
        <>
          <Button style="primary" size="large" fullWidth onClick={onContinue}>
            {t("personalisationInfoCards.meetYourAdvisor")}
          </Button>
          <TextButton variant="secondary" onClick={onSkip}>
            {t("personalisationInfoCards.skipForNow")}
          </TextButton>
        </>
      }
      contentClassName="pt-[30%]"
    >
      <PersonalisationHeading size="sm">
        {t("personalisationInfoCards.welcome")}
        <br /> <br />
        {t("personalisationInfoCards.subtitle")}
      </PersonalisationHeading>

      <div className="flex flex-col gap-4">
        {/* Card animations are delayed to sequence after screen load animation (0.1s-0.6s) */}
        {cards.map((card, index) => (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: ANIMATION_DURATION,
              delay: 0.6 + index * 0.25,
            }}
            className="flex flex-row items-center gap-3 rounded-2xl bg-white/60 p-4 shadow-subtle backdrop-blur-sm"
            key={index}
          >
            <div className="size-8 shrink-0">
              <Icon svg={card.icon} size="medium" color="brandColor" />
            </div>
            <div className="font-sans text-body1 text-primary-neutral">{card.text}</div>
          </motion.div>
        ))}
      </div>
    </PersonalisationScreenLayout>
  );
};
